import { useEffect } from 'react';

interface ScreenLock { release: () => Promise<void>; }
interface ScreenLockNavigator { wakeLock?: { request: (type: 'screen') => Promise<ScreenLock> }; }

export function useGameWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const manager = (navigator as Navigator & ScreenLockNavigator).wakeLock;
    if (!manager) return;
    let lock: ScreenLock | undefined, disposed = false, pending = false;
    let retryAfter = 0;
    const acquire = async () => {
      if (disposed || pending || lock || document.visibilityState !== 'visible' || Date.now() < retryAfter) return;
      pending = true;
      try {
        const requested = await manager.request('screen');
        if (disposed) { await requested.release(); return; }
        lock = requested;
        (requested as ScreenLock & EventTarget).addEventListener?.('release', () => { if (lock === requested) lock = undefined; });
      } catch { retryAfter = Date.now() + 10000; }
      finally { pending = false; }
    };
    const visibility = () => {
      if (document.visibilityState === 'visible') { retryAfter = 0; void acquire(); }
      else if (lock) { const previous = lock; lock = undefined; void previous.release().catch(() => {}); }
    };
    void acquire();
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('fullscreenchange', acquire);
    document.addEventListener('pointerup', acquire);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('fullscreenchange', acquire);
      document.removeEventListener('pointerup', acquire);
      if (lock) void lock.release().catch(() => {});
    };
  }, [enabled]);
}
